import { Check } from 'lucide-react';
import Seo from '../components/Seo';
import PageHero from '../components/PageHero';
import SectionHeading from '../components/SectionHeading';
import Reveal from '../components/Reveal';
import ArrowLink from '../components/ArrowLink';
import Marquee from '../components/Marquee';
import { LogoMark } from '../components/Logo';
import { IMAGES, TRADES } from '../data/content';

const APPROACH = [
  {
    title: 'Plain English only',
    text: 'If we can’t explain something without jargon, we don’t say it. You’ll always know what you’re paying for and why.',
  },
  {
    title: 'Fixed, published prices',
    text: 'The prices on this site are the prices. Anything extra is quoted first, in writing, and only with your agreement.',
  },
  {
    title: 'One person, start to finish',
    text: 'You deal directly with the person doing the work. Questions get answered by someone who actually knows your project.',
  },
  {
    title: 'You own it all',
    text: 'Your domain, your website, your profiles — held in your name wherever the platform allows. No hostages, no lock-in.',
  },
];

export default function AboutPage() {
  return (
    <>
      <Seo
        title="About — Built for Tradespeople | Build My Trade"
        description="Build My Trade exists to make getting online simple for UK tradespeople: straightforward packages, clear pricing and personal support."
        path="/about"
      />
      <PageHero
        eyebrow="About Build My Trade"
        title="Built for tradespeople. Designed to make getting online simple."
        intro="Running a trade business is demanding enough without having to figure out websites, Google listings and social media."
        crumbs={[{ label: 'About' }]}
        testid="about-heading"
      />

      <section className="bg-offwhite" data-testid="about-story">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <Reveal>
              <div className="relative overflow-hidden rounded-2xl">
                <img
                  src={IMAGES.carpentry.url}
                  alt={IMAGES.carpentry.alt}
                  loading="lazy"
                  className="aspect-[4/5] w-full object-cover"
                />
              </div>
            </Reveal>
            <div>
              <SectionHeading
                eyebrow="Why we exist"
                title="Good tradespeople shouldn’t lose work to a bad website — or no website"
                testid="about-story-heading"
              />
              <Reveal delay={0.1}>
                <div className="mt-6 space-y-5 text-base leading-relaxed text-stonebrand">
                  <p>
                    Build My Trade exists to make the online part simpler. We help independent tradespeople and small
                    trade businesses establish a professional online presence through straightforward packages, clear
                    pricing and personal support.
                  </p>
                  <p>
                    Most of the people we work with have built their reputation on word of mouth and quality work —
                    and have neither the time nor the inclination to learn websites, social media or digital
                    marketing. That’s exactly as it should be. Your job is the trade; ours is making sure customers
                    can find you, trust you and reach you.
                  </p>
                  <p>
                    Everything is designed around that idea: fixed packages instead of open-ended quotes, plain
                    English instead of agency jargon, and a setup service that handles the lot rather than leaving
                    you to coordinate five different providers.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <Marquee items={TRADES} dark={false} />

      {/* How we work */}
      <section className="border-b border-hairline bg-white" data-testid="about-approach">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <SectionHeading
            eyebrow="How we work"
            title="A straight service, run properly"
            testid="about-approach-heading"
          />
          <div className="mt-14 grid gap-5 sm:grid-cols-2">
            {APPROACH.map((a, i) => (
              <Reveal key={i} delay={0.07 * i} className="h-full">
                <div className="h-full rounded-2xl border border-hairline bg-offwhite-light p-8">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-lime">
                    <Check className="h-5 w-5 text-charcoal" aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-bold tracking-tight text-charcoal">{a.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stonebrand">{a.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Founder note — personalised by Saf, July 2026 */}
      <section className="bg-charcoal" data-testid="about-founder">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-3xl">
            <Reveal>
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">A personal service</p>
              <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-offwhite sm:text-3xl lg:text-4xl">
                You’ll deal directly with the person building your online presence
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-8 rounded-2xl border border-charcoal-line bg-charcoal-surface p-8 sm:p-10">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-lime">
                  <LogoMark className="h-8 w-8 text-charcoal" />
                </div>
                <p className="mt-7 text-base leading-relaxed text-offwhite/80 sm:text-lg">
                  “I started Build My Trade with a simple idea: every hardworking tradesperson deserves the
                  opportunity to present their business professionally online.
                </p>
                <p className="mt-5 text-base leading-relaxed text-offwhite/80 sm:text-lg">
                  Every tradesperson and every business is different, so I’ve built the whole service around a
                  personal approach — you deal with me directly, from your first enquiry right through to launch. No
                  sales team, no handoffs, no being passed around.
                </p>
                <p className="mt-5 text-base leading-relaxed text-offwhite/80 sm:text-lg">
                  My aim is simple: to make getting online as straightforward as possible, so you can get on with
                  the work you’re actually brilliant at.”
                </p>
                <p className="mt-8 flex items-center gap-4 border-t border-charcoal-line pt-6">
                  <span className="font-heading text-base font-bold text-offwhite">Saf</span>
                  <span className="text-sm text-offwhite/50">Founder, Build My Trade</span>
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-offwhite bg-blueprint-light" data-testid="about-cta">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-2xl text-center">
            <Reveal>
              <h2 className="text-balance font-heading text-3xl font-extrabold tracking-tight text-charcoal sm:text-4xl">
                Sound like the way you’d want it handled?
              </h2>
              <p className="mt-5 text-base leading-relaxed text-stonebrand">
                Send a free, no-obligation enquiry and tell us where your business is today.
              </p>
              <div className="mt-9">
                <ArrowLink to="/contact" variant="dark" testid="about-cta-quote" className="px-8 py-4">
                  Get Your Free Quote
                </ArrowLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
